import React from 'react';
import { useSessionStore } from '../stores/sessionStore';
import { hasModuleAccess, SYSTEM_MODULES, getPlanLabel } from '../config/moduleRegistry';
import { ShieldAlert, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { hasPlatformAccess } from '../features/auth/platformAccess';

interface ModuleGuardProps {
  moduleId?: string;
  adminOnly?: boolean;
  children: React.ReactNode;
}

export function ModuleGuard({ moduleId, adminOnly = false, children }: ModuleGuardProps) {
  const user = useSessionStore((state) => state.user);
  const navigate = useNavigate();

  const isAdmin = hasPlatformAccess(user);

  if (adminOnly && !isAdmin) {
    return (
      <div style={{ padding: '3rem', maxWidth: '600px', margin: '4rem auto', textAlign: 'center' }}>
        <div style={{ 
          background: 'rgba(239, 68, 68, 0.1)', 
          border: '1px solid rgba(239, 68, 68, 0.2)', 
          borderRadius: '16px', 
          padding: '2.5rem', 
          boxShadow: '0 12px 32px rgba(0,0,0,0.3)' 
        }}>
          <ShieldAlert size={48} color="#ef4444" style={{ margin: '0 auto 1.5rem auto' }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem', color: '#fff' }}>
            Acesso Restrito ao Administrador
          </h2>
          <p style={{ color: 'var(--text-muted, #94a3b8)', lineHeight: 1.6, marginBottom: '2rem' }}>
            Esta área é exclusiva para administradores da plataforma e gerenciamento global.
          </p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="primary-button"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0.75rem 1.5rem' }}
          >
            <ArrowLeft size={16} /> Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (moduleId && !hasModuleAccess(user, moduleId)) {
    const moduleDef = SYSTEM_MODULES.find(m => m.id === moduleId);
    const moduleName = moduleDef?.name || moduleId;
    const planName = getPlanLabel(user?.plan);

    return (
      <div style={{ padding: '3rem', maxWidth: '640px', margin: '4rem auto', textAlign: 'center' }}>
        <div style={{ 
          background: 'rgba(99, 102, 241, 0.08)', 
          border: '1px solid rgba(99, 102, 241, 0.25)', 
          borderRadius: '20px', 
          padding: '2.5rem', 
          boxShadow: '0 16px 40px rgba(0,0,0,0.3)' 
        }}>
          <ShieldAlert size={48} color="#6366f1" style={{ margin: '0 auto 1.5rem auto' }} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem', color: '#fff' }}>
            Módulo Não Contemplado pelo Plano
          </h2>
          <p style={{ color: 'var(--text-muted, #94a3b8)', lineHeight: 1.6, marginBottom: '0.5rem' }}>
            O módulo <strong>"{moduleName}"</strong> não está contemplado no seu plano atual (<strong>{planName}</strong>) para a organização {user?.tenant_name || user?.tenant_id}.
          </p>
          <p style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '0.875rem', marginBottom: '2rem' }}>
            Para liberar esta funcionalidade, solicite a ativação de opcionais no <strong>Plano Flex</strong> ou faça o upgrade para o <strong>Plano Growth com IA</strong>.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/dashboard')}
              className="secondary-button"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0.75rem 1.25rem' }}
            >
              <ArrowLeft size={16} /> Voltar ao Dashboard
            </button>
            <button 
              onClick={() => navigate('/ai-packages')}
              className="primary-button"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0.75rem 1.25rem' }}
            >
              Ver Central de Planos & IA
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
